import React, { useState, useEffect, useCallback } from 'react';
import { Sidebar } from '../includes/sidebar';
import { Topbar } from '../includes/topbar';
import { useTranslation } from 'react-i18next';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import {
    Card,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
    Search, 
    CheckCircle2, 
    XCircle, 
    Calendar,
    FileText, 
    User, 
    Eye,
    MessageSquare,
    Loader2,
    Download,
    AlertTriangle,
    Table as TableIcon
} from 'lucide-react';
import { rapportsApi, AgentRapport, RapportStatus } from '../../api/rapports.api';
import { useToast } from '../../hooks/use-toast';
import { cn } from '../../lib/utils';
import { 
    Dialog, 
    DialogContent, 
    DialogDescription, 
    DialogFooter, 
    DialogHeader, 
    DialogTitle 
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import * as XLSX from 'xlsx';
import axios from 'axios';

const formatHTG = (amount: string | number) => {
    return new Intl.NumberFormat('fr-HT', { style: 'currency', currency: 'HTG' }).format(Number(amount));
};

export default function RapportManagementPage() {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [rapports, setRapports] = useState<AgentRapport[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<RapportStatus>('pending');
    const [searchTerm, setSearchTerm] = useState('');
    
    // Modal state for validation
    const [selectedRapport, setSelectedRapport] = useState<AgentRapport | null>(null);
    const [actionType, setActionType] = useState<'validate' | 'reject' | null>(null);
    const [adminNotes, setAdminNotes] = useState('');
    const [processingAction, setProcessingAction] = useState(false);

    // Modal state for viewing report data
    const [viewingRapport, setViewingRapport] = useState<AgentRapport | null>(null);
    const [excelData, setExcelData] = useState<any[][]>([]);
    const [loadingExcel, setLoadingExcel] = useState(false);

    const loadRapports = useCallback(async () => {
        try {
            setLoading(true);
            const response = await rapportsApi.list({ status: statusFilter });
            setRapports(response?.results || []);
        } catch (err) {
            console.error('Error loading rapports:', err);
            toast({ title: t('dashboard:rapports.toast.error_load'), description: t('dashboard:rapports.toast.error_load'), type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [statusFilter, t, toast]);

    useEffect(() => {
        loadRapports();
    }, [statusFilter, loadRapports]);

    const handleAction = async () => {
        if (!selectedRapport || !actionType) return;

        try {
            setProcessingAction(true);
            await rapportsApi.validate(selectedRapport.id, actionType, adminNotes);
            
            toast({ 
                title: t('dashboard:messages.dataUpdated'), 
                description: actionType === 'validate' ? t('dashboard:rapports.toast.success_validate') : t('dashboard:rapports.toast.success_reject'), 
                type: 'success' 
            });
            
            // Refresh list and close modal
            setSelectedRapport(null);
            setActionType(null);
            setAdminNotes('');
            loadRapports();
        } catch (err: any) {
            console.error('Error processing action:', err);
            toast({ 
                title: t('dashboard:kyc.errorTitle'), 
                description: err.response?.data?.error || t('dashboard:rapports.toast.error_action'), 
                type: 'error' 
            });
        } finally {
            setProcessingAction(false);
        }
    };

    const viewExcel = async (rapport: AgentRapport) => {
        setViewingRapport(rapport);
        setExcelData([]);
        
        if (!rapport.file) {
            // Do not return early. We want to show the new summary regardless of the excel file.
            setLoadingExcel(false);
            return;
        }

        try {
            setLoadingExcel(true);

            // Fetch the file without internal API interceptors (prevents R2 from rejecting Bearer tokens)
            const response = await axios.get(rapport.file, { responseType: 'arraybuffer' });
            const data = new Uint8Array(response.data);
            const workbook = XLSX.read(data, { type: 'array' });
            
            // Read the first sheet
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            
            // Convert to JSON (array of arrays)
            const json: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            setExcelData(json);
        } catch (err) {
            console.error('Error reading Excel:', err);
            toast({ title: t('dashboard:kyc.errorTitle'), description: t('dashboard:rapports.toast.error_excel'), type: 'error' });
        } finally {
            setLoadingExcel(false);
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'pending': return <Badge variant="outline" className="bg-amber-50 text-amber-600 border-amber-200">{t('dashboard:rapports.filters.pending')}</Badge>;
            case 'validated': return <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">{t('dashboard:rapports.filters.validated')}</Badge>;
            case 'rejected': return <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">{t('dashboard:rapports.filters.rejected')}</Badge>;
            default: return <Badge variant="outline">{status}</Badge>;
        }
    };

    const filteredRapports = (rapports || []).filter(r => 
        (r?.agent_name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
        (r?.date_rapport || '').includes(searchTerm)
    );

    return (
        <div className="flex h-screen overflow-hidden bg-slate-50/30">
            <Sidebar />
            <div className="flex-1 flex flex-col overflow-hidden">
                <Topbar />
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-6">
                    {/* Breadcrumb */}
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem>
                                <BreadcrumbLink>{t('dashboard:sidebar.items.admin')}</BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem>
                                <BreadcrumbPage>{t('dashboard:rapports.title')}</BreadcrumbPage>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>

                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">{t('dashboard:rapports.title')}</h1>
                            <p className="text-slate-500 text-sm mt-1">
                                {t('dashboard:rapports.description')}
                            </p>
                        </div>
                    </div>

                    {/* Filters & Navigation */}
                    <div className="flex flex-col md:flex-row gap-4 items-end">
                        <div className="flex flex-1 items-center gap-2 bg-white p-1 rounded-xl border border-slate-200 shadow-sm w-full md:w-auto">
                            <button 
                                onClick={() => setStatusFilter('pending')}
                                className={cn(
                                    "flex-1 px-6 py-2 rounded-lg text-sm font-medium transition-all",
                                    statusFilter === 'pending' ? "bg-amber-500 text-white shadow-md font-bold" : "text-slate-600 hover:bg-slate-50"
                                )}
                            >
                                {t('dashboard:rapports.filters.pending')}
                            </button>
                            <button 
                                onClick={() => setStatusFilter('validated')}
                                className={cn(
                                    "flex-1 px-6 py-2 rounded-lg text-sm font-medium transition-all",
                                    statusFilter === 'validated' ? "bg-green-600 text-white shadow-md font-bold" : "text-slate-600 hover:bg-slate-50"
                                )}
                            >
                                {t('dashboard:rapports.filters.validated')}
                            </button>
                            <button 
                                onClick={() => setStatusFilter('rejected')}
                                className={cn(
                                    "flex-1 px-6 py-2 rounded-lg text-sm font-medium transition-all",
                                    statusFilter === 'rejected' ? "bg-red-600 text-white shadow-md font-bold" : "text-slate-600 hover:bg-slate-50"
                                )}
                            >
                                {t('dashboard:rapports.filters.rejected')}
                            </button>
                        </div>

                        <div className="relative w-full md:w-80">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <Input 
                                placeholder={t('dashboard:rapports.search_placeholder')} 
                                className="pl-10 h-11 border-slate-200 focus:ring-primary-500 rounded-xl bg-white shadow-sm"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Reports Grid/Table */}
                    <Card className="border-none shadow-sm overflow-hidden bg-white/50 backdrop-blur-sm">
                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50/80 border-b border-slate-200">
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">{t('dashboard:rapports.table.agent_date')}</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 text-center">{t('dashboard:rapports.table.transactions')}</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">{t('dashboard:rapports.table.deposit')}</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">{t('dashboard:rapports.table.withdrawal')}</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">{t('dashboard:rapports.table.cash')}</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">{t('dashboard:rapports.table.status')}</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">{t('dashboard:rapports.table.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 bg-white/30">
                                    {loading ? (
                                        <tr>
                                            <td colSpan={7} className="px-6 py-12 text-center">
                                                <Loader2 className="h-8 w-8 animate-spin text-primary-500 mx-auto" />
                                                <p className="text-slate-500 mt-2">{t('dashboard:rapports.loading')}</p>
                                            </td>
                                        </tr>
                                    ) : filteredRapports.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="px-6 py-12 text-center">
                                                <div className="bg-slate-100/50 rounded-full h-12 w-12 flex items-center justify-center mx-auto text-slate-300">
                                                    <FileText className="h-6 w-6" />
                                                </div>
                                                <p className="text-slate-500 mt-2">{t('dashboard:rapports.empty')}</p>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredRapports.map((rapport) => (
                                            <tr key={rapport.id} className="hover:bg-slate-50/80 transition-colors group">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center border-2 border-white shadow-sm">
                                                            <User className="h-5 w-5 text-primary-600" />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-bold text-slate-900 group-hover:text-primary-600 transition-colors">
                                                                {rapport.agent_name || 'Agent inconnu'}
                                                            </p>
                                                            <p className="text-xs text-slate-400 mt-0.5 font-medium">
                                                                {new Date(rapport.date_rapport).toLocaleDateString('fr-FR', {
                                                                    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                                                                })}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <Badge variant="secondary" className="bg-slate-100 text-slate-600 font-mono">
                                                        {rapport.nb_transactions} TX
                                                    </Badge>
                                                </td>
                                                <td className="px-6 py-4 font-bold text-slate-900">
                                                    <div className="flex flex-col">
                                                                <span className="text-emerald-600 font-medium">{formatHTG(rapport.total_depo_bs)}</span>
                                                                <span className="text-[10px] text-slate-400 uppercase tracking-tighter">(System BS)</span>
                                                            </div>
                                                            <div className="flex flex-col mt-2">
                                                                <span className="text-emerald-600 font-medium">{formatHTG(rapport.total_depo_sol)}</span>
                                                                <span className="text-[10px] text-slate-400 uppercase tracking-tighter">(System SOL)</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 font-bold text-slate-900 border-x">
                                                            <div className="flex flex-col">
                                                                <span className="text-rose-600 font-medium">{formatHTG(rapport.total_retrait_bs)}</span>
                                                                <span className="text-[10px] text-slate-400 uppercase tracking-tighter">(System BS)</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 border-r">
                                                            <div className="flex flex-col">
                                                                <span className="text-slate-700 font-medium">{formatHTG(rapport.montant_caisse_bs || 0)}</span>
                                                                <span className="text-[10px] text-slate-400 uppercase tracking-tighter">(Caisse BS)</span>
                                                            </div>
                                                            <div className="flex flex-col mt-2">
                                                                <span className="text-slate-700 font-medium">{formatHTG(rapport.montant_caisse_sol || 0)}</span>
                                                                <span className="text-[10px] text-slate-400 uppercase tracking-tighter">(Caisse SOL)</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 border-r align-top">
                                                            <div className="flex flex-col gap-2 items-start mt-1">
                                                                {getStatusBadge(rapport.status)}
                                                                
                                                                <div className="flex flex-col gap-1 w-full mt-1">
                                                                    <div className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm w-max ${Number(rapport.difference_bs) === 0 ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
                                                                        BS: {Number(rapport.difference_bs) === 0 ? 'ÉQUILIBRÉ' : '⚠ DIFFÉRENCE'}
                                                                    </div>
                                                                    <div className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm w-max ${Number(rapport.difference_sol) === 0 ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
                                                                        SOL: {Number(rapport.difference_sol) === 0 ? 'ÉQUILIBRÉ' : '⚠ DIFFÉRENCE'}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 text-right">
                                                            <div className="flex items-center justify-end gap-2">
                                                                <Button 
                                                                    variant="outline" 
                                                                    size="sm" 
                                                                    className="h-9 w-9 p-0 rounded-lg border-slate-200 hover:bg-primary-50 hover:text-primary-600"
                                                                    onClick={() => viewExcel(rapport)}
                                                                    title="Visualiser Excel"
                                                                >
                                                                    <Eye className="h-4 w-4" />
                                                                </Button>
                                                                
                                                                {rapport.status === 'pending' && (
                                                                    <>
                                                                        <Button 
                                                                            size="sm" 
                                                                            className="bg-green-600 hover:bg-green-700 h-9 gap-1.5 shadow-sm rounded-lg"
                                                                            onClick={() => {
                                                                                setSelectedRapport(rapport);
                                                                                setActionType('validate');
                                                                            }}
                                                                        >
                                                                            <CheckCircle2 className="h-4 w-4" />
                                                                            {t('dashboard:rapports.action.validate')}
                                                                        </Button>
                                                                        <Button 
                                                                            size="sm" 
                                                                            variant="outline"
                                                                            className="text-red-600 border-red-100 hover:bg-red-50 hover:text-red-700 h-9 gap-1.5 rounded-lg"
                                                                            onClick={() => {
                                                                                setSelectedRapport(rapport);
                                                                                setActionType('reject');
                                                                            }}
                                                                        >
                                                                            <XCircle className="h-4 w-4" />
                                                                            {t('dashboard:rapports.action.reject')}
                                                                        </Button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
        
                                {/* Mobile Card View */}
                                <div className="md:hidden divide-y divide-slate-100">
                                    {loading ? (
                                        <div className="px-6 py-12 text-center">
                                            <Loader2 className="h-8 w-8 animate-spin text-primary-500 mx-auto" />
                                            <p className="text-slate-500 mt-2">{t('dashboard:rapports.loading')}</p>
                                        </div>
                                    ) : filteredRapports.length === 0 ? (
                                        <div className="px-6 py-12 text-center">
                                            <div className="bg-slate-100/50 rounded-full h-12 w-12 flex items-center justify-center mx-auto text-slate-300">
                                                <FileText className="h-6 w-6" />
                                            </div>
                                            <p className="text-slate-500 mt-2">{t('dashboard:rapports.empty')}</p>
                                        </div>
                                    ) : (
                                        filteredRapports.map((rapport) => (
                                            <div key={rapport.id} className="p-4 space-y-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="h-10 w-10 rounded-full bg-primary-50 flex items-center justify-center text-primary-700 font-bold border border-primary-100">
                                                            {rapport?.agent_name?.charAt(0) || '?'}
                                                        </div>
                                                        <div>
                                                            <div className="font-bold text-slate-900 leading-tight">{rapport?.agent_name}</div>
                                                            <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                                                                <Calendar className="h-3 w-3" />
                                                                {rapport?.date_rapport}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col items-end gap-1.5">
                                                        {getStatusBadge(rapport.status)}
                                                        <div className="flex gap-1 mt-1">
                                                            <span className={`text-[9px] font-bold px-1 py-0.5 border rounded-sm ${Number(rapport.difference_bs) === 0 ? 'bg-green-50 text-green-700 border-green-100' : 'bg-red-50 text-red-700 border-red-100'}`}>
                                                                BS:{Number(rapport.difference_bs) === 0 ? 'OK' : '⚠'}
                                                            </span>
                                                            <span className={`text-[9px] font-bold px-1 py-0.5 border rounded-sm ${Number(rapport.difference_sol) === 0 ? 'bg-green-50 text-green-700 border-green-100' : 'bg-red-50 text-red-700 border-red-100'}`}>
                                                                SOL:{Number(rapport.difference_sol) === 0 ? 'OK' : '⚠'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-3 pb-2">
                                                    <div className="bg-slate-50 p-2 rounded-lg border border-slate-100">
                                                        <p className="text-[10px] uppercase font-bold text-slate-400 mb-1">{t('dashboard:rapports.table.deposit')} (BS)</p>
                                                        <p className="text-sm font-bold text-emerald-600">{formatHTG(rapport.total_depo_bs)}</p>
                                                        <p className="text-[10px] uppercase font-bold text-slate-400 mt-2 mb-1">{t('dashboard:rapports.table.deposit')} (SOL)</p>
                                                        <p className="text-sm font-bold text-emerald-600">{formatHTG(rapport.total_depo_sol)}</p>
                                                    </div>
                                                    <div className="bg-slate-50 p-2 rounded-lg border border-slate-100 flex flex-col gap-2">
                                                        <div>
                                                            <p className="text-[10px] uppercase font-bold text-slate-400 mb-1">{t('dashboard:rapports.table.withdrawal')} (BS)</p>
                                                            <p className="text-sm font-bold text-rose-600">{formatHTG(rapport.total_retrait_bs)}</p>
                                                        </div>
                                                        <div className="pt-2 border-t border-slate-200 border-dashed">
                                                            <p className="text-[10px] uppercase font-bold text-slate-400 mb-1">{t('dashboard:rapports.table.cash')} (BS & SOL)</p>
                                                            <p className="text-xs font-bold text-slate-700">{formatHTG(rapport.montant_caisse_bs || 0)} / {formatHTG(rapport.montant_caisse_sol || 0)}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-2 pt-2 border-t border-slate-50">
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm" 
                                                        className="flex-1 gap-2 rounded-lg"
                                                        onClick={() => viewExcel(rapport)}
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                        {t('dashboard:rapports.action.view_details')}
                                                    </Button>

                                                    {rapport.status === 'pending' && (
                                                        <div className="flex gap-2 flex-[2]">
                                                            <Button 
                                                                size="sm" 
                                                                className="flex-1 gap-1.5 bg-green-600 hover:bg-green-700 rounded-lg"
                                                                onClick={() => {
                                                                    setSelectedRapport(rapport);
                                                                    setActionType('validate');
                                                                }}
                                                            >
                                                                <CheckCircle2 className="h-4 w-4" />
                                                                {t('dashboard:rapports.action.validate')}
                                                            </Button>
                                                            <Button 
                                                                size="sm" 
                                                                variant="outline"
                                                                className="flex-1 gap-1.5 text-red-600 border-red-100 hover:bg-red-50 rounded-lg"
                                                                onClick={() => {
                                                                    setSelectedRapport(rapport);
                                                                    setActionType('reject');
                                                                }}
                                                            >
                                                                <XCircle className="h-4 w-4" />
                                                                {t('dashboard:rapports.action.reject')}
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    )}
                        </div>
                    </Card>
                </div>
            </div>

            {/* Excel Viewer Dialog */}
            <Dialog 
                open={!!viewingRapport} 
                onOpenChange={(open) => {
                    if(!open) setViewingRapport(null);
                }}
            >
                <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0 overflow-hidden">
                    <DialogHeader className="p-6 border-b bg-slate-50">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-emerald-100 rounded-lg">
                                    <TableIcon className="h-5 w-5 text-emerald-600" />
                                </div>
                                <div>
                                    <DialogTitle>{t('dashboard:rapports.dialog.view_excel')}</DialogTitle>
                                    <DialogDescription>
                                        {t('dashboard:rapports.dialog.excel_description', { name: viewingRapport?.agent_name })}
                                    </DialogDescription>
                                </div>
                            </div>
                            {viewingRapport?.file && (
                                <Button variant="outline" size="sm" className="gap-2" asChild>
                                    <a href={viewingRapport.file} download target="_blank" rel="noopener noreferrer">
                                        <Download className="h-4 w-4" />
                                        {t('dashboard:rapports.dialog.download')}
                                    </a>
                                </Button>
                            )}
                        </div>
                    </DialogHeader>
                    
                    <div className="flex-1 overflow-auto p-0 bg-white min-h-[50vh]">
                        {loadingExcel ? (
                            <div className="flex flex-col items-center justify-center h-full">
                                <Loader2 className="h-10 w-10 animate-spin text-primary-500" />
                                <p className="text-slate-500 mt-4 font-medium italic">{t('dashboard:messages.loading')}</p>
                            </div>
                        ) : excelData.length > 0 ? (
                            <div className="relative">
                                <table className="w-full text-left border-collapse text-xs">
                                    <thead className="sticky top-0 bg-slate-100 shadow-sm z-10">
                                        <tr>
                                            {excelData[0].map((cell, i) => (
                                                <th key={i} className="px-4 py-3 font-bold text-slate-700 border-x border-slate-200">
                                                    {cell}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {excelData.slice(1).map((row, i) => (
                                            <tr key={i} className="hover:bg-slate-50 transition-colors">
                                                {excelData[0].map((_, j) => (
                                                    <td key={j} className="px-4 py-2 text-slate-600 border-x border-slate-50">
                                                        {row[j] !== undefined ? String(row[j]) : ''}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full bg-slate-50/50 p-6 space-y-6">
                                <div className="text-center">
                                    <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto" />
                                    <p className="text-slate-600 mt-2 font-medium">{t('dashboard:rapports.dialog.no_excel_data')}</p>
                                    <p className="text-xs text-slate-400 max-w-sm mt-1 mx-auto">
                                        Fichye Excel sa pa disponib oswa li pat atache lè rapò sa te voye a. Men ou genyen detay tranzaksyon yo pi ba a.
                                    </p>
                                </div>
                            </div>
                        )}
                        
                        {/* Summary Block Displayed at Bottom of Scroll or Center if no Excel */}
                        <div className="p-6 bg-slate-50 border-t border-slate-200 grid grid-cols-1 md:grid-cols-2 gap-6 mt-auto">
                            
                            {/* SOL Section */}
                            <div className="space-y-4 bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                                <h4 className="text-[13px] font-bold text-slate-900 border-b pb-3 flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-blue-500"></div> SÒL (MITYÈL)
                                </h4>
                                
                                <div className="space-y-3 text-[13px]">
                                    <div className="flex justify-between items-center border-b border-slate-50 pb-2">
                                        <span className="text-slate-500">{t('dashboard:rapports.dialog.total_deposit')}</span>
                                        <span className="font-bold text-slate-900">{formatHTG(viewingRapport?.total_depo_sol || 0)}</span>
                                    </div>
                                    <div className="flex justify-between items-center border-b border-slate-50 pb-2">
                                        <span className="text-slate-500 font-medium">{t('dashboard:rapports.dialog.expected_balance')}</span>
                                        <span className="font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100">{formatHTG(viewingRapport?.montant_attendu_sol || 0)}</span>
                                    </div>
                                    <div className="flex justify-between items-center bg-slate-50 -mx-2 px-2 py-2 rounded">
                                        <span className="text-slate-700 font-bold">💰 {t('dashboard:rapports.dialog.caisse_bs')}</span>
                                        <span className={`font-bold px-2 py-0.5 rounded border ${Number(viewingRapport?.difference_sol) === 0 ? 'bg-green-100 text-green-800 border-green-200' : 'bg-amber-100 text-amber-800 border-amber-200'}`}>
                                            {formatHTG(viewingRapport?.montant_caisse_sol || 0)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center pt-1">
                                        <span className="text-slate-700 font-bold">📊 {t('dashboard:rapports.dialog.difference')}</span>
                                        <span className={`font-bold ${Number(viewingRapport?.difference_sol) === 0 ? 'text-green-600' : 'text-red-500'}`}>
                                            {formatHTG(viewingRapport?.difference_sol || 0)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center pt-1">
                                        <span className="text-slate-500">{t('dashboard:rapports.dialog.status')}</span>
                                        <span className={`font-bold text-xs uppercase ${Number(viewingRapport?.difference_sol) === 0 ? 'text-green-600' : 'text-red-500'}`}>
                                            {Number(viewingRapport?.difference_sol) === 0 ? '✅ ÉQUILIBRÉ' : '⚠ DIFFÉRENCE'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* BS Section */}
                            <div className="space-y-4 bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                                <h4 className="text-[13px] font-bold text-slate-900 border-b pb-3 flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-emerald-500"></div> BOUSANM SOLIDÈ (BS)
                                </h4>
                                
                                <div className="space-y-3 text-[13px]">
                                    <div className="flex justify-between items-center border-b border-slate-50 pb-2">
                                        <span className="text-slate-500">{t('dashboard:rapports.dialog.total_deposit')}</span>
                                        <span className="font-bold text-slate-900">{formatHTG(viewingRapport?.total_depo_bs || 0)}</span>
                                    </div>
                                    <div className="flex justify-between items-center border-b border-slate-50 pb-2">
                                        <span className="text-slate-500">{t('dashboard:rapports.dialog.total_withdrawal')}</span>
                                        <span className="font-bold text-rose-600">{formatHTG(viewingRapport?.total_retrait_bs || 0)}</span>
                                    </div>
                                    <div className="flex justify-between items-center border-b border-slate-50 pb-2">
                                        <span className="text-slate-500 font-medium">{t('dashboard:rapports.dialog.expected_balance')}</span>
                                        <span className="font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100">{formatHTG(viewingRapport?.montant_attendu_bs || 0)}</span>
                                    </div>
                                    <div className="flex justify-between items-center bg-slate-50 -mx-2 px-2 py-2 rounded">
                                        <span className="text-slate-700 font-bold">💰 {t('dashboard:rapports.dialog.caisse_bs')}</span>
                                        <span className={`font-bold px-2 py-0.5 rounded border ${Number(viewingRapport?.difference_bs) === 0 ? 'bg-green-100 text-green-800 border-green-200' : 'bg-amber-100 text-amber-800 border-amber-200'}`}>
                                            {formatHTG(viewingRapport?.montant_caisse_bs || 0)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center pt-1">
                                        <span className="text-slate-700 font-bold">📊 {t('dashboard:rapports.dialog.difference')}</span>
                                        <span className={`font-bold ${Number(viewingRapport?.difference_bs) === 0 ? 'text-green-600' : 'text-red-500'}`}>
                                            {formatHTG(viewingRapport?.difference_bs || 0)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center pt-1">
                                        <span className="text-slate-500">{t('dashboard:rapports.dialog.status')}</span>
                                        <span className={`font-bold text-xs uppercase ${Number(viewingRapport?.difference_bs) === 0 ? 'text-green-600' : 'text-red-500'}`}>
                                            {Number(viewingRapport?.difference_bs) === 0 ? '✅ ÉQUILIBRÉ' : '⚠ DIFFÉRENCE'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Agent Notes */}
                            <div className="col-span-1 md:col-span-2 mt-2">
                                <h4 className="text-[13px] font-bold text-slate-900 mb-2">📝 NOTE DE L'AGENT</h4>
                                <div className="bg-white p-3 rounded-lg border border-slate-200 text-sm text-slate-700 whitespace-pre-wrap min-h-[60px] italic">
                                    {viewingRapport?.notes || <span className="text-slate-400 not-italic">Pa gen nòt pou rapò sa a</span>}
                                </div>
                            </div>
                        </div>

                    </div>
                </DialogContent>
            </Dialog>

            {/* Validation Action Dialog */}
            <Dialog 
                open={!!selectedRapport && !!actionType} 
                onOpenChange={(open) => {
                    if(!open) {
                        setSelectedRapport(null);
                        setActionType(null);
                        setAdminNotes('');
                    }
                }}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {actionType === 'validate' ? (
                                <>
                                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                                    <span>{t('dashboard:rapports.action.validate')}</span>
                                </>
                            ) : (
                                <>
                                    <XCircle className="h-5 w-5 text-red-600" />
                                    <span>{t('dashboard:rapports.action.reject')}</span>
                                </>
                            )}
                        </DialogTitle>
                        <DialogDescription>
                            {actionType === 'validate' 
                                ? t('dashboard:rapports.action.confirm_desc_validate', { amount: formatHTG(selectedRapport?.total_depo_bs || 0), name: selectedRapport?.agent_name })
                                : t('dashboard:rapports.action.confirm_desc_reject', { amount: formatHTG(selectedRapport?.total_depo_bs || 0), name: selectedRapport?.agent_name })}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                                <MessageSquare className="h-4 w-4 text-slate-400" />
                                {t('dashboard:rapports.action.notes_label')}
                            </label>
                            <Textarea 
                                placeholder={t('dashboard:rapports.action.notes_placeholder')}
                                className="resize-none min-h-[100px] border-slate-200 focus:ring-primary-500 rounded-xl"
                                value={adminNotes}
                                onChange={(e) => setAdminNotes(e.target.value)}
                            />
                        </div>
                    </div>

                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button 
                            variant="outline" 
                            onClick={() => {
                                setSelectedRapport(null);
                                setActionType(null);
                                setAdminNotes('');
                            }}
                            className="border-slate-200 rounded-lg px-6"
                        >
                            {t('dashboard:rapports.action.cancel')}
                        </Button>
                        <Button 
                            className={cn(
                                "gap-2 shadow-md rounded-lg px-6",
                                actionType === 'validate' ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"
                            )}
                            onClick={handleAction}
                            disabled={processingAction}
                        >
                             {processingAction ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                                actionType === 'validate' ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />
                            )}
                            {actionType === 'validate' ? t('dashboard:rapports.action.confirm_validate') : t('dashboard:rapports.action.confirm_reject')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
